import './style.scss'
import { Button, Drawer, Input, message, Modal, Radio, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'
import * as Api from '@/api/home'
import IconCreationEmpty from '@/assets/icon-creation-empty.png'
import IconPicture from '@/assets/icon-picture.png'
import Logo from '@/assets/logo.webp'
import { CreateVideo } from '@/assets/svg'
import { IconRandom } from '@/assets/svg/icon-random'
import { IconSave } from '@/assets/svg/icon-save'
import { IconScore } from '@/assets/svg/icon-score'
import UserCredits from '@/components/UserCredits'
import VideoModal from '@/components/VideoModal'
import { UserStore } from '@/global-states'
import { checkSensitive, downlad, getCreatePhotoRandomTexts, Render, urlSource } from '@/libs/util'
import { DeleteOutlined, DownloadOutlined, LoadingOutlined } from '@ant-design/icons'
import PhotoLibrary from '../PhotoLibrary'

interface IProps {
  open: boolean
  group?: any
  mode?: number
  photo?: any
  onCancel?: () => void
}

const CreateAiMaterialDrawer: FC<IProps> = (props) => {
  const { open, group, mode, photo, onCancel } = props
  const navigate = useNavigate()
  const { t } = useTranslation(['system', 'avatar'])
  const [results, setResults] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [detailPrompt, setDetailPrompt] = useState('')
  const [style, setStyle] = useState(1)
  const [size, setSize] = useState(1)
  const [createNum, setCreateNum] = useState(4)
  const [imageCost, setImageCost] = useState(0)
  const [tabIndex, setTabIndex] = useState(1)
  const [providerList, setProviderList] = useState<any[]>([])
  const [videoModeIndex, setVideoModeIndex] = useState(0)
  const [photoInfo, setPhotoInfo] = useState<any>()
  const resultTimerRef = useRef<any>()
  const detailPromptRef = useRef<any>()
  const [randomKey, setRandomKey] = useState<any>()
  const lastRef = useRef<any>()
  const [photoLibraryOpen, setPhotoLibraryOpen] = useState(false)
  const [preview, setPreview] = useState<any>()
  const [saveInfo, setSaveInfo] = useState<any>()
  const [saveLoading, setSaveLoading] = useState(false)

  const showResults = useMemo(() => {
    if (tabIndex === 1) {
      return results?.filter((s: any) => s.type === 1)
    }

    if (tabIndex === 2) {
      return results?.filter((s: any) => s.type === 2)
    }
  }, [tabIndex, results])

  useEffect(() => {
    lastRef.current?.scrollIntoView()
  }, [showResults?.length])

  const videoCost = useMemo(() => {
    const current = providerList?.[videoModeIndex]
    return current?.discounted_credits
  }, [videoModeIndex, providerList])

  const imageNumCost = useMemo(() => {
    return createNum * (imageCost || 0)
  }, [createNum, imageCost])

  useEffect(() => {
    if (open) {
      getResults()
      getPhotoPrice()
      getPhotoToVideoPrice()
      setDetailPrompt('')
      setTabIndex(mode || 1)
      setLoading(false)
      setPhotoInfo(photo)
      setRandomText()
      UserStore.initSensitiveWords()
    } else {
      setTimeout(() => {
        setResults([])
      }, 100)
    }

    return () => {
      cleaEvent()
      setPhotoInfo(undefined)
    }
  }, [open])

  const getResults = async () => {
    const res = await Api.getDigitalHumanGroupTasks(group.id, {
      page: 0,
      size: 500
    })
    setResults(
      (res?.list || [])
        .map((l: any) => ({
          ...l,
          results:
            l.status === 3
              ? [...(l.results || []), ...new Array(l.quantity - (l.results?.length || 0))]
              : [...new Array(l.quantity || 1)]
        }))
        .reverse()
    )
    if (res?.list?.some((l: any) => l.status === 1)) {
      loopResults()
    }
  }

  const getPhotoPrice = async () => {
    const res = await Api.getImageToImageCost()
    setImageCost(res?.credits_per_image || 0)
  }

  const getPhotoToVideoPrice = async () => {
    const res = await Api.getPictureAvatarInfo()
    setProviderList(res?.provider_info_list || [])
  }

  const cleaEvent = () => {
    if (resultTimerRef.current) {
      clearInterval(resultTimerRef.current)
    }
  }

  const setRandomText = () => {
    const r: any = getCreatePhotoRandomTexts().filter((t) => t.key !== randomKey)
    const index = Math.floor(Math.random() * r.length)
    setRandomKey(r[index].key)
    setDetailPrompt(r[index].text)
  }

  const loopResults = async () => {
    cleaEvent()
    resultTimerRef.current = setInterval(async () => {
      const res = await Api.getDigitalHumanGroupTasks(group.id, {
        page: 0,
        size: 500
      })
      setResults(
        (res?.list || [])
          .map((l: any) => ({
            ...l,
            results:
              l.status === 3
                ? [...(l.results || []), ...new Array(l.quantity - (l.results?.length || 0))]
                : [...new Array(l.quantity || 1)]
          }))
          .reverse()
      )
    }, 3000)
  }

  const onSubmitPhoto = async () => {
    if (!detailPrompt?.trim()) {
      detailPromptRef.current?.focus()
      return message.warning(t('avatar.input_detail_prompt'))
    }

    const errorWords = checkSensitive([{ words: detailPrompt }])

    if (errorWords?.length) {
      return message.error(
        <div style={{ padding: '12px' }}>
          {t('avatar.sensitive_words_warning')}
          <label className="red">（{errorWords.join('、')}</label>），{t('avatar.modify_and_retry')}
        </div>
      )
    }

    setLoading(true)

    setResults((r) => [
      ...(r || []),
      {
        type: 1,
        status: 1,
        prompt: detailPrompt,
        image_size_type: size,
        results: [...new Array(createNum)],
        create_time: Date.now() / 1000
      }
    ])

    setTimeout(() => {
      setLoading(false)
    }, 1000)

    cleaEvent()

    const res = await Api.submitImageToImage(group.id, {
      detail_prompt: detailPrompt,
      size,
      quantity: createNum,
      style
    })

    UserStore.updateUserPackage()

    if (res.task_id) {
      loopResults()
    }
  }

  const onSubmitVideo = async () => {
    if (!photoInfo?.id) {
      return message.warning(t('avatar.select_photo_material'))
    }

    setLoading(true)

    setResults((r) => [
      ...(r || []),
      {
        type: 2,
        status: 1,
        results: [...new Array(1)],
        create_time: Date.now() / 1000
      }
    ])

    setTimeout(() => {
      setLoading(false)
    }, 1000)

    const res = await Api.submitImageToVideo(group.id, {
      image_id: photoInfo?.id,
      live_portrait_provider: providerList?.[videoModeIndex]?.provider
    })
    UserStore.updateUserPackage()
    if (res.task_id) {
      loopResults()
    }
  }

  const toCreateVideo = async (d: any) => {
    if (d?.saved_id) {
      navigate(`/create-video/${d.saved_id}`)
    } else {
      const res = await Api.saveVideoToDigitalHumans(d.task_id)
      if (res?.digital_human_id) {
        navigate(`/create-video/${res.digital_human_id}`)
      }
    }
  }

  const sureSave = async () => {
    try {
      setSaveLoading(true)
      if (saveInfo?.type === 1) {
        const res = await Api.savePhotoToDigitalHumans(saveInfo.task_id, {
          oss_key: saveInfo.oss_key
        })
        await Api.updateDigitalPhoto(group.id, res?.digital_human_image_id, {
          title: saveInfo.title
        })
      } else {
        const res = await Api.saveVideoToDigitalHumans(saveInfo.task_id)
        await Api.updateDigitalHumans(res?.digital_human_id, {
          title: saveInfo.title
        })
      }
      message.success(t('system.submit_success'))
      setSaveInfo(undefined)
    } finally {
      setSaveLoading(false)
    }
  }

  return (
    <Drawer
      className="create-ai-material-drawer "
      open={open}
      title={t('avatar.ai_generated_materials')}
      width={'100%'}
      onClose={onCancel}
      footer={null}
    >
      <div className="drawer-wrapper">
        <div className="wrapper-left">
          <div className="tab">
            <div className={`tab-item ${tabIndex === 1 ? 'actived gradient' : ''}`} onClick={() => setTabIndex(1)}>
              {t('avatar.generate_photo_materials')}
            </div>
            <div className={`tab-item ${tabIndex === 2 ? 'actived gradient' : ''}`} onClick={() => setTabIndex(2)}>
              {t('avatar.generate_video_materials')}
            </div>
          </div>
          {tabIndex === 1 ? (
            <>
              <div className="form">
                <div className="form-item">
                  <div className="title">{t('avatar.avatar')}</div>
                  <div className="content">
                    <div className="header-photo">
                      <img src={group?.face_url || group?.cover_url} />
                      <div className="title"></div>
                    </div>
                  </div>
                </div>

                <div className="form-item">
                  <div className="title">
                    <label className="red">*</label>
                    <label>{t('avatar.detail_description')}</label>
                  </div>
                  <div className="content">
                    <div className="textarea-box">
                      <Input.TextArea
                        ref={detailPromptRef}
                        value={detailPrompt}
                        onChange={(e) => setDetailPrompt(e.target.value)}
                        placeholder={t('avatar.input_face_details')}
                        showCount
                        maxLength={300}
                      ></Input.TextArea>
                      <Button icon={<IconRandom />} className="btn" type="primary" onClick={setRandomText}>
                        {t('avatar.random_generate')}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="form-item">
                  <div className="title">{t('avatar.select_style')}</div>
                  <div className="content">
                    <Radio.Group onChange={(e) => setStyle(e.target.value)} value={style}>
                      <Radio value={1}>{t('avatar.realistic')}</Radio>
                      <Radio value={2}>{t('avatar.cartoon')}</Radio>
                      <Radio value={3}>{t('avatar.ghibli')}</Radio>
                      <Radio value={4}>{t('avatar.warm_film')}</Radio>
                      <Radio value={5}>{t('avatar.soft_focus')}</Radio>
                    </Radio.Group>
                  </div>
                </div>

                <div className="form-item">
                  <div className="title">{t('avatar.size')}</div>
                  <div className="content">
                    <div className="size-list">
                      <div className={`list-item ${size === 1 ? 'actived' : ''}`} onClick={() => setSize(1)}>
                        <span className={size === 1 ? 'gradient' : ''}>
                          <label className="line"></label>
                          <label>9:16</label>
                        </span>
                      </div>
                      <div className={`list-item ${size === 2 ? 'actived' : ''}`} onClick={() => setSize(2)}>
                        <span className={size === 2 ? 'gradient' : ''}>
                          <label className="line hor"></label>
                          <label>16:9</label>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="footer">
                <div className="num-choose">
                  <div className="title">{t('avatar.generate_quantity')}</div>
                  <div className="list">
                    <div className={`list-item ${createNum === 1 ? 'actived' : ''}`} onClick={() => setCreateNum(1)}>
                      <span className={createNum === 1 ? 'gradient' : ''}>1</span>
                    </div>
                    <div className={`list-item ${createNum === 2 ? 'actived' : ''}`} onClick={() => setCreateNum(2)}>
                      <span className={createNum === 2 ? 'gradient' : ''}>2</span>
                    </div>
                    <div className={`list-item ${createNum === 3 ? 'actived' : ''}`} onClick={() => setCreateNum(3)}>
                      <span className={createNum === 3 ? 'gradient' : ''}>3</span>
                    </div>
                    <div className={`list-item ${createNum === 4 ? 'actived' : ''}`} onClick={() => setCreateNum(4)}>
                      <span className={createNum === 4 ? 'gradient' : ''}>4</span>
                    </div>
                  </div>
                </div>

                <Button type="primary" loading={loading} onClick={onSubmitPhoto}>
                  <span>{t('avatar.generate_now')}</span>

                  {!!imageNumCost && (
                    <span className="cost">
                      <IconScore className="icon-score" />
                      <label>
                        {imageNumCost}
                        {t('avatar.points')}
                      </label>
                    </span>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="form">
                <div className="form-item">
                  <div className="title">{t('avatar.avatar')}</div>
                  <div className="content">
                    <div className="header-photo">
                      <img src={group?.face_url || group?.cover_url} />
                      <div className="title"></div>
                    </div>
                  </div>
                </div>

                <div className="form-item">
                  <div className="title">
                    <label className="red">*</label>
                    <label>{t('avatar.photo_materials')}</label>
                  </div>
                  <div className="content">
                    <div
                      className="photo-box"
                      onClick={() => {
                        setPhotoLibraryOpen(true)
                      }}
                    >
                      {photoInfo?.url ? (
                        <>
                          <img className="img" src={photoInfo?.url} />
                          <div
                            className="delete-box"
                            onClick={(e) => {
                              e.stopPropagation()
                              setPhotoInfo(undefined)
                            }}
                          >
                            <DeleteOutlined />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="pic">
                            <img src={IconPicture} />
                          </div>
                          <div className="desc">{t('avatar.select_from_library')}</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="footer">
                <div className="num-choose">
                  <div className="title">{t('avatar.generate_model')}</div>
                  <div className="list">
                    {providerList?.slice(0, 2)?.map((p, index) => (
                      <div
                        key={index}
                        className={`list-item ${videoModeIndex === index ? 'actived' : ''}`}
                        onClick={() => setVideoModeIndex(index)}
                      >
                        <span className={videoModeIndex === index ? 'gradient' : ''}>{p.title}</span>
                        {index === 0 && <span className="tag">new</span>}
                      </div>
                    ))}
                  </div>
                </div>

                <Button type="primary" loading={loading} onClick={onSubmitVideo}>
                  {loading ? <span>{t('avatar.generating')}</span> : <span>{t('avatar.generate_now')}</span>}

                  {!!videoCost && (
                    <span className="cost">
                      <IconScore className="icon-score" />
                      <label>
                        {videoCost}
                        {t('avatar.points')}
                      </label>
                    </span>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
        <div className="wrapper-right">
          {showResults?.length ? (
            <div className="result-list">
              {showResults?.map((r, index) => (
                <div key={index} className="result-list__item">
                  <div className="header">
                    <div className="header__left">
                      <img src={Logo} />
                    </div>
                    <div className="header__right">
                      <div className="name">{t('avatar.flying_shadow_ai')}</div>
                      <div className="time">{dayjs(r.create_time * 1000).format('YYYY/MM/DD HH:mm')}</div>
                    </div>
                  </div>
                  {r.prompt && <div className="desc">{r.prompt}</div>}

                  {r?.status === 3 ? (
                    <>
                      <div className="media-list">
                        {r?.results?.map((d: any, index: number) => (
                          <>
                            <Render>
                              {function Load() {
                                const [loadingIndex, setLoadingIndex] = useState(-1)
                                const [isHovered, setIsHovered] = useState(false)
                                const [ref, inView] = useInView({ triggerOnce: true })
                                const [percent, setPercent] = useState(-1)
                                const xhrRef = useRef<any>()

                                const downladMedia = (d: any, e: any) => {
                                  e.stopPropagation()
                                  if (xhrRef.current) {
                                    xhrRef.current.abort()
                                    setPercent(0)
                                  }
                                  const xhr: XMLHttpRequest = downlad(d.url, '')
                                  xhr.onprogress = (e) => {
                                    const p = Math.floor((e.loaded / e.total) * 100) // 下载进度
                                    console.log('进度', p)
                                    setPercent(p)
                                    if (p === 100) {
                                      setTimeout(() => {
                                        setPercent(-1)
                                      }, 300)
                                    }
                                  }
                                  xhrRef.current = xhr
                                }

                                const toCreateVideoMaterial = async (d: any) => {
                                  try {
                                    if (d?.saved_id) {
                                      setPhotoInfo({
                                        ...d,
                                        id: d.saved_id
                                      })
                                      setLoadingIndex(-1)
                                      setTabIndex(2)
                                    } else {
                                      const res = await Api.savePhotoToDigitalHumans(r.task_id, {
                                        oss_key: d?.oss_key
                                      })
                                      if (res?.digital_human_image_id) {
                                        setPhotoInfo({
                                          ...d,
                                          id: res.digital_human_image_id
                                        })
                                        setTabIndex(2)
                                      }
                                    }
                                  } finally {
                                    setLoadingIndex(-1)
                                  }
                                }

                                return (
                                  <div
                                    key={index}
                                    className={`media-list__item ${
                                      r?.image_size_type === 1 ? 'portrait' : 'landscape'
                                    } ${r?.type === 1 ? 'picture' : 'video'}`}
                                    onMouseEnter={() => setIsHovered(true)}
                                    onMouseLeave={() => setIsHovered(false)}
                                    ref={ref}
                                  >
                                    {d?.url ? (
                                      <div
                                        className="box"
                                        onClick={() => {
                                          if (r.type === 2) {
                                            setPreview({
                                              url: d?.url
                                            })
                                          }
                                        }}
                                      >
                                        {inView && (
                                          <>
                                            {r.type === 2 && isHovered ? (
                                              <video
                                                poster={urlSource(d?.url, 'video')}
                                                autoPlay
                                                controls={false}
                                                loop
                                                className="media"
                                                src={d.url}
                                              />
                                            ) : (
                                              <img
                                                className="media"
                                                src={urlSource(d?.url, r.type === 2 ? 'video' : '')}
                                              />
                                            )}
                                          </>
                                        )}

                                        <div className="mask"></div>

                                        <div className="op">
                                          <div className="op-box"></div>
                                        </div>

                                        <div className="op" onClick={(e) => e.stopPropagation()}>
                                          <Tooltip placement="top" title={t('system.download')}>
                                            <div className="btn" onClick={(e) => downladMedia(d, e)}>
                                              <DownloadOutlined />
                                            </div>
                                          </Tooltip>

                                          <Tooltip
                                            placement="top"
                                            title={r.type === 1 ? t('avatar.save_to_photo') : t('avatar.save_to_video')}
                                          >
                                            <div
                                              className="btn"
                                              onClick={() =>
                                                setSaveInfo({
                                                  ...r,
                                                  oss_key: d.oss_key,
                                                  title: t('avatar.unnamed')
                                                })
                                              }
                                            >
                                              <IconSave className="icon-save" />
                                            </div>
                                          </Tooltip>
                                        </div>

                                        {percent > -1 && (
                                          <div className="pending">
                                            <LoadingOutlined />
                                            <div>
                                              {t('system.downloading')} {percent}%
                                            </div>
                                          </div>
                                        )}

                                        {r.type === 1 && (
                                          <Button
                                            icon={<CreateVideo />}
                                            className="select-btn"
                                            type="primary"
                                            loading={loadingIndex === index}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              toCreateVideoMaterial(d)
                                              setLoadingIndex(index)
                                            }}
                                          >
                                            {t('avatar.generate_video_material')}
                                          </Button>
                                        )}

                                        {r.type === 2 && (
                                          <Button
                                            className="select-btn"
                                            type="primary"
                                            icon={<CreateVideo />}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              toCreateVideo({
                                                ...d,
                                                task_id: r.task_id
                                              })
                                            }}
                                          >
                                            {t('avatar.create_work')}
                                          </Button>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="box">
                                        <div className="loading">
                                          <div className="loading-text">{t('avatar.content_blocked')}</div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )
                              }}
                            </Render>
                          </>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="media-list">
                        {r?.results?.map((d: any, i: number) => (
                          <div
                            key={i}
                            className={`media-list__item ${r?.image_size_type === 1 ? 'portrait' : 'landscape'} ${
                              r?.type === 1 ? 'picture' : 'video'
                            }`}
                          >
                            <div className="box">
                              {r.status === 2 ? (
                                <div className="loading">
                                  <div className="loading-text">{t('avatar.generate_failed')}</div>
                                </div>
                              ) : (
                                <div className="loading">
                                  <LoadingOutlined />
                                  <div className="loading-text">{t('avatar.generating')}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ))}

              <div ref={lastRef}></div>
            </div>
          ) : (
            <div className={`empty`}>
              <img src={IconCreationEmpty} />
              <div className="desc">{t('avatar.create_prompt')}</div>
            </div>
          )}
        </div>
      </div>

      <UserCredits />

      <PhotoLibrary
        selectId={photoInfo?.id}
        group={group}
        open={photoLibraryOpen}
        onCancel={() => setPhotoLibraryOpen(false)}
        onCheck={(p) => setPhotoInfo(p)}
      />

      <Modal
        open={!!saveInfo?.type}
        title={saveInfo?.type === 1 ? t('avatar.save_to_photo') : t('avatar.save_to_video')}
        onCancel={() => setSaveInfo(undefined)}
        onOk={sureSave}
        okButtonProps={{
          disabled: !saveInfo?.title?.trim(),
          loading: saveLoading
        }}
      >
        <div style={{ padding: 20 }}>
          <Input
            style={{ height: 40 }}
            maxLength={20}
            value={saveInfo?.title}
            onChange={(e) =>
              setSaveInfo({
                ...saveInfo,
                title: e.target.value
              })
            }
          />
        </div>
      </Modal>

      <VideoModal preview={preview} onCancel={() => setPreview(undefined)} btnShow={false} />
    </Drawer>
  )
}

export default CreateAiMaterialDrawer
